'use client';

import type { ElementType, ReactElement, ReactNode } from 'react';
import { isValidElement } from 'react';
import type {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
} from 'ag-grid-community';
import { HStack } from '@terra/ui-styled-system';
import { Text } from './text';
import {
  BodyDefault,
  BodyLarge,
  BodyStrong,
  HeadingHero,
  HeadingItem,
  HeadingModal,
  HeadingPage,
  HeadingSection,
  LabelDefault,
  MetaDefault,
  MonoDefault,
  MonoSmall,
} from './typography';

const TABLE_CELL_TYPOGRAPHY_ELEMENTS = new Set<ElementType>([
  HeadingHero,
  HeadingPage,
  HeadingModal,
  HeadingSection,
  HeadingItem,
  BodyLarge,
  BodyDefault,
  BodyStrong,
  LabelDefault,
  MetaDefault,
  MonoDefault,
  MonoSmall,
  Text,
]);

const isTypographyTableCellElement = (
  node: ReactNode,
): node is ReactElement<{ truncate?: boolean }> =>
  isValidElement(node) &&
  TABLE_CELL_TYPOGRAPHY_ELEMENTS.has(node.type as ElementType);

export function wrapTableCellContent(content: ReactNode): ReactNode {
  if (!isTypographyTableCellElement(content)) {
    return content;
  }

  const truncate = content.props.truncate === true;

  return (
    <HStack
      h="full"
      alignItems="center"
      minW={truncate ? 0 : undefined}
      w={truncate ? 'full' : undefined}
    >
      {content}
    </HStack>
  );
}

const isColumnGroupDef = <T,>(
  columnDef: ColDef<T> | ColGroupDef<T>,
): columnDef is ColGroupDef<T> =>
  Array.isArray((columnDef as ColGroupDef<T>).children);

export const wrapColumnDefsWithTableCellLayout = <T,>(
  columnDefs: (ColDef<T> | ColGroupDef<T>)[] | null | undefined,
): (ColDef<T> | ColGroupDef<T>)[] | null | undefined => {
  if (!columnDefs) {
    return columnDefs;
  }

  return columnDefs.map((columnDef) => {
    if (isColumnGroupDef(columnDef)) {
      return {
        ...columnDef,
        children: wrapColumnDefsWithTableCellLayout(columnDef.children) ?? [],
      };
    }

    const { cellRenderer, ...rest } = columnDef;

    if (typeof cellRenderer !== 'function') {
      return columnDef;
    }

    return {
      ...rest,
      cellRenderer: (params: ICellRendererParams<T>) => {
        if (params.data == null) {
          return null;
        }

        return wrapTableCellContent(cellRenderer(params));
      },
    };
  });
};
